import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Button, Form, Alert, Table } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { Context } from '../index';
import { fetchOneProduct, createRating } from '../http/productAPI';
import { observer } from 'mobx-react-lite';
import { FaStar } from 'react-icons/fa';
import '../styles/ProductPage.css';
import { useToast } from '../contexts/ToastContext';
import { getImageUrl, buildPictureSources, getThumbUrl } from '../utils/imageUtils';

const ProductPage = observer(() => {
    const { user } = useContext(Context);
    const { showToast } = useToast();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [ratingError, setRatingError] = useState('');
    const [ratingSuccess, setRatingSuccess] = useState('');
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedVariantIdx, setSelectedVariantIdx] = useState(-1);
    const [sliderIdx, setSliderIdx] = useState(0);

    const renderResponsivePicture = (imgPath, width = 900, imgProps = {}) => {
        const { avif, webp, fallback } = buildPictureSources(imgPath, width);
        const safeFallback = fallback || getImageUrl(imgPath);
        return (
            <picture>
                {avif && <source srcSet={avif} type="image/avif" />}
                {webp && <source srcSet={webp} type="image/webp" />}
                <img
                    src={safeFallback}
                    loading="lazy"
                    decoding="async"
                    alt={imgProps.alt || 'Фото товара'}
                    {...imgProps}
                    onError={e => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getImageUrl(imgPath);
                    }}
                />
            </picture>
        );
    };

    const getThumbSrc = (imgPath, width = 160) => {
        const thumb = getThumbUrl(imgPath, width, 'orig');
        return thumb || getImageUrl(imgPath);
    };

    const refreshProduct = useCallback(async () => {
        setLoading(true);
        try {
            const updated = await fetchOneProduct(id);
            setProduct(updated);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchOneProduct(id)
            .then(data => {
                // Логируем variants для отладки
                if (data.variants && Array.isArray(data.variants)) {
                    data.variants.forEach((v, idx) => {
                        console.log(`[ProductPage] variant[${idx}]:`, v);
                    });
                }
                setProduct(data);
                setLoading(false);
            })
            .catch(e => {
                setError('Ошибка при загрузке товара');
                setLoading(false);
            });
        // === ПАТЧ: слушаем BroadcastChannel для авто-обновления ===
        const bc = new window.BroadcastChannel('product_update');
        bc.onmessage = (event) => {
            if (event.data && event.data.productId === id) {
                refreshProduct();
            }
        };
        return () => bc.close();
    }, [id, refreshProduct]);

    const handleAddToBasket = async () => {
        if (!user.isAuth) {
            navigate('/login');
            return;
        }
        const needsVariantSelection = (product.type === 'shoe' || product.type === 'clothes' || product.type === 'toy');
        if (needsVariantSelection && selectedVariantIdx === -1 && (product.variants || []).length > 1) {
            setError('Пожалуйста, выберите вариант товара');
            return;
        }
        // Получаем выбранный вариант
        let variant = null;
        if (product.variants && product.variants.length > 0) {
            if (selectedVariantIdx >= 0 && product.variants[selectedVariantIdx]) {
                // Используем выбранный вариант
                variant = product.variants[selectedVariantIdx];
            } else if (product.variants.length === 1) {
                // Если только один вариант, используем его
                variant = product.variants[0];
            } else {
                // Если вариантов больше одного, но не выбран - ошибка
                setError('Пожалуйста, выберите вариант товара');
                return;
            }
        }
        
        // Проверяем количество выбранного варианта для игрушек
        if (product.type === 'toy' && variant && (variant.quantity || 0) <= 0) {
            setError('Выбранный вариант товара отсутствует в наличии');
            return;
        }
        
        let store_id = null;
        let store_name = null;
        let variantForBasket = variant;
        if (product.type === 'toy') {
            if (variant) {
                store_id = variant.storeId || (product.product_stores?.[0]?.storeId ?? null);
                if (variant.storeName) {
                    store_name = variant.storeName;
                } else if (store_id && product.stores && Array.isArray(product.stores)) {
                    const storeObj = product.stores.find(s => s.id === store_id);
                    if (storeObj) store_name = storeObj.address || storeObj.name;
                }
                variantForBasket = { ...variant, storeId: store_id, storeName: store_name };
            } else if (Array.isArray(product.product_stores) && product.product_stores.length > 0) {
                // Фоллбек на старый формат
                const ps = product.product_stores[0];
                store_id = ps.storeId;
                if (ps.store && (ps.store.address || ps.store.name)) {
                    store_name = ps.store.address || ps.store.name;
                } else if (product.stores && Array.isArray(product.stores)) {
                    const storeObj = product.stores.find(s => s.id === store_id);
                    if (storeObj) store_name = storeObj.address || storeObj.name;
                }
                variantForBasket = { storeId: store_id, storeName: store_name, quantity: product.toy?.quantity || ps.quantity || 0 };
            }
        } else {
            store_id = variant?.storeId || null;
            if (variant && variant.storeName) {
                store_name = variant.storeName;
            } else if (store_id && product.stores && Array.isArray(product.stores)) {
                const storeObj = product.stores.find(s => s.id === store_id);
                if (storeObj) {
                    store_name = storeObj.address || storeObj.name;
                }
            }
        }
        user.addToBasket(product, variantForBasket, quantity, store_id);
        setError('');
        showToast('Товар добавлен в корзину', 'success');
        await refreshProduct();
    };

    const handleRatingSubmit = async (e) => {
        e.preventDefault();
        if (!user.isAuth) {
            navigate('/login');
            return;
        }
        if (rating === 0) {
            setRatingError('Пожалуйста, выберите оценку');
            return;
        }
        try {
            await createRating(product.id, rating, comment);
            setRatingSuccess('Спасибо за ваш отзыв!');
            setRatingError('');
            setRating(0);
            setComment('');
            // Обновляем данные продукта
            const updatedProduct = await fetchOneProduct(id);
            setProduct(updatedProduct);
        } catch (e) {
            console.error('Ошибка при отправке отзыва:', e);
            setRatingError(e.response?.data?.message || 'Ошибка при отправке отзыва');
        }
    };

    // Функция для получения storeName по storeId
    const getStoreName = (variant) => {
        if (variant.storeName) return variant.storeName;
        if (variant.storeId && product && Array.isArray(product.stores)) {
            const foundStore = product.stores.find(s => s.id === variant.storeId);
            if (foundStore) return foundStore.name;
        }
        return '-';
    };

    const handlePrev = () => setSliderIdx(idx => (idx - 1 + images.length) % images.length);
    const handleNext = () => setSliderIdx(idx => (idx + 1) % images.length);

    if (loading) {
        return <div className="loading-spinner" />;
    }

    if (!product) {
        return <div>Товар не найден</div>;
    }

    let images = [];
    if (Array.isArray(product.images) && product.images.length > 0) {
        images = product.images.filter(Boolean);
    } else if (typeof product.images === 'string' && product.images) {
        try {
            const parsed = JSON.parse(product.images);
            if (Array.isArray(parsed)) {
                images = parsed.filter(Boolean);
            } else if (typeof parsed === 'string') {
                images = [parsed];
            }
        } catch {
            images = [product.images];
        }
    } else if (product.image_url) {
        images = [product.image_url];
    } else {
        images = [];
    }
    // Убираем пустые/undefined значения
    images = images.filter(img => !!img);
    // Лог для отладки
    console.log('[ProductPage] Итоговый массив images:', images);

    // === Проверка остатка товара (универсально для игрушек и других типов) ===
    // Для товаров с вариантами проверяем количество выбранного варианта
    let stock = 0;
    let isOutOfStock = false;
    
    if (product.type === 'toy' && Array.isArray(product.variants) && product.variants.length > 0) {
        // Для игрушек с вариантами проверяем количество выбранного варианта
        if (selectedVariantIdx >= 0 && product.variants[selectedVariantIdx]) {
            stock = product.variants[selectedVariantIdx].quantity || 0;
        } else if (selectedVariantIdx === -1 && product.variants.length > 0) {
            // Если вариант не выбран, берем первый доступный вариант
            const availableVariant = product.variants.find(v => (v.quantity || 0) > 0);
            stock = availableVariant ? availableVariant.quantity : 0;
        } else {
            stock = 0;
        }
        isOutOfStock = stock <= 0;
    } else if (Array.isArray(product.product_stores) && product.product_stores.length > 0) {
        stock = product.product_stores.reduce((sum, ps) => sum + (ps.quantity || 0), 0);
        isOutOfStock = stock <= 0;
    } else if (product.type === 'toy') {
        if (typeof product.quantity === 'number') {
            stock = product.quantity;
        } else if (product.toy && typeof product.toy.quantity === 'number') {
            stock = product.toy.quantity;
        } else if (typeof product.stock === 'number') {
            stock = product.stock;
        }
        isOutOfStock = stock <= 0;
    } else if (typeof product.stock === 'number') {
        stock = product.stock;
        isOutOfStock = stock <= 0;
    } else {
        isOutOfStock = true;
    }

    return (
        <Container className="product-page mt-4">
            <Row>
                <Col md={6}>
                    <div className="product-image-container" style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: 0,
                        padding: 0,
                        border: 'none',
                        minHeight: 0
                    }}>
                        {images.length > 0 && (
                            <div style={{
                                position: 'relative',
                                width: '100%',
                                maxWidth: 500,
                                minHeight: 500,
                                height: 500,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                                background: '#fff',
                                borderRadius: 12,
                                overflow: 'hidden'
                            }}>
                                {renderResponsivePicture(images[sliderIdx], 900, {
                                    className: 'product-page-image',
                                    alt: product.name,
                                    style: { width: '100%', height: '100%', objectFit: 'contain', aspectRatio: '1/1' }
                                })}
                                {/* Стрелки */}
                                {images.length > 1 && (
                                    <>
                                        <button
                                            className="slider-arrow left"
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: 10,
                                                transform: 'translateY(-50%)',
                                                background: 'rgba(0,0,0,0.3)',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: 36,
                                                height: 36,
                                                fontSize: 22,
                                                cursor: 'pointer',
                                                zIndex: 2
                                            }}
                                            onClick={handlePrev}
                                            aria-label="Предыдущее фото"
                                        >&lt;</button>
                                        <button
                                            className="slider-arrow right"
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                right: 10,
                                                transform: 'translateY(-50%)',
                                                background: 'rgba(0,0,0,0.3)',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: 36,
                                                height: 36,
                                                fontSize: 22,
                                                cursor: 'pointer',
                                                zIndex: 2
                                            }}
                                            onClick={handleNext}
                                            aria-label="Следующее фото"
                                        >&gt;</button>
                                    </>
                                )}
                                {/* Точки */}
                                {images.length > 1 && (
                                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', gap: 8 }}>
                                        {images.map((img, idx) => (
                                            <span
                                                key={idx}
                                                style={{
                                                    display: 'inline-block',
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: '50%',
                                                    background: sliderIdx === idx ? '#007bff' : '#ccc',
                                                    cursor: 'pointer',
                                                    border: '1px solid #888'
                                                }}
                                                onClick={() => setSliderIdx(idx)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {/* Мобильная адаптация */}
                    <style>{`
                        @media (max-width: 768px) {
                            .product-image-container {
                                minHeight: 220px !important;
                            }
                            .product-image {
                                max-width: 100% !important;
                                max-height: 220px !important;
                                min-height: 220px !important;
                                height: 220px !important;
                                object-fit: cover !important;
                            }
                            .slider-arrow.left, .slider-arrow.right {
                                width: 32px !important;
                                height: 32px !important;
                                font-size: 18px !important;
                            }
                        }
                    `}</style>
                    {/* Миниатюры */}
                    {images.length > 1 && (
                        <div style={{ marginTop: 16, display: 'flex', gap: 12, justifyContent: 'center', alignItems: 'center' }}>
                            {images.map((img, idx) => (
                                <img
                                    key={idx}
                                    src={getThumbSrc(img, 180)}
                                    alt={`thumb-${idx}`}
                                    loading="lazy"
                                    decoding="async"
                                    style={{
                                        width: 64,
                                        height: 64,
                                        objectFit: 'cover',
                                        borderRadius: 8,
                                        border: sliderIdx === idx ? '2px solid #007bff' : '1px solid #ccc',
                                        cursor: 'pointer',
                                        boxShadow: sliderIdx === idx ? '0 0 8px #007bff55' : 'none',
                                        opacity: sliderIdx === idx ? 1 : 0.7,
                                        transition: 'all 0.2s',
                                        background: '#fff',
                                        padding: 2
                                    }}
                                    onClick={() => setSliderIdx(idx)}
                                />
                            ))}
                        </div>
                    )}
                </Col>
                <Col md={6}>
                    <div className="product-info">
                        <h1 className="product-title">{product.name}</h1>
                        <div className="product-type">
                            {product.type === 'shoe' ? 'Обувь' : 
                             product.type === 'toy' ? 'Игрушка' : 
                             product.type === 'clothes' ? 'Одежда' : product.type}
                        </div>
                        <div className="product-price">
                            {product.discount > 0 ? (
                                <div className="product-price-with-discount">
                                    <div className="discounted-price-main">
                                        {(product.price * (1 - product.discount / 100)).toLocaleString()} ₽
                                    </div>
                                    <div className="original-price-main">
                                        {product.price.toLocaleString()} ₽
                                    </div>
                                    <div className="discount-badge-main">
                                        -{product.discount}%
                                    </div>
                                </div>
                            ) : (
                                <span className="regular-price">{product.price.toLocaleString()} ₽</span>
                            )}
                        </div>
                        {/* Отображение артикула для обуви и одежды */}
                        {(product.type === 'shoe' || product.type === 'clothes') && (
                            (product.type === 'shoe' && product.article) || 
                            (product.type === 'clothes' && product.clothes?.length > 0 && product.clothes[0].article)
                        ) && (
                            <div className="product-article mb-3">
                                <strong>Артикул:</strong> {product.type === 'shoe' ? product.article : product.clothes[0].article}
                            </div>
                        )}
                        {/* Добавляем отображение материала для одежды */}
                        {product.type === 'clothes' && product.clothes?.length > 0 && product.clothes[0].material && (
                            <div className="product-material mb-3">
                                <strong>Материал:</strong> {product.clothes[0].material}
                            </div>
                        )}
                        {/* Добавляем отображение материала и возрастных ограничений для игрушек */}
                        {product.type === 'toy' && (
                            <div className="toy-details mb-3">
                                {product.toy?.material && (
                                    <div className="product-material mb-2">
                                        <strong>Материал:</strong> {product.toy.material}
                                    </div>
                                )}
                                {product.toy?.age_rating && (
                                    <div className="product-age-rating mb-2">
                                        <strong>Возрастные ограничения:</strong> {product.toy.age_rating}
                                    </div>
                                )}
                            </div>
                        )}
                        {product.description && (
                            <div className="product-description mb-3">
                                <h4>Описание товара:</h4>
                                <p>{product.description}</p>
                            </div>
                        )}
                        {(product.type === 'shoe' || product.type === 'clothes') && product.variants && product.variants.length > 0 && (
                            <>
                                <div className="size-table-container">
                                    <h4>Доступные варианты:</h4>
                                    <Table striped bordered hover>
                                        <thead>
                                            <tr>
                                                <th>Размер</th>
                                                <th>Цвет</th>
                                                <th>Магазин</th>
                                                <th>Количество</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {product.variants.map((v, idx) => (
                                                <tr key={idx} style={{background: selectedVariantIdx === idx ? '#e6f7ff' : undefined}} onClick={() => { setSelectedVariantIdx(idx); console.log('[ProductPage] Выбран вариант:', v); }}>
                                                    <td>{v.size}</td>
                                                    <td>{v.color}</td>
                                                    <td>{getStoreName(v)}</td>
                                                    <td>{v.quantity}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                {/* Отображение адреса магазина для выбранного варианта */}
                                {selectedVariantIdx >= 0 && product.variants[selectedVariantIdx] && (
                                    <div className="mb-2">
                                        <b>Адрес магазина:</b> {getStoreName(product.variants[selectedVariantIdx])}
                                    </div>
                                )}
                                <Form.Group className="mb-3">
                                    <Form.Label>Выберите вариант</Form.Label>
                                    <Form.Select
                                        value={selectedVariantIdx}
                                        onChange={e => setSelectedVariantIdx(Number(e.target.value))}
                                    >
                                        <option value={-1}>Выберите вариант</option>
                                        {product.variants.map((v, idx) => (
                                            <option key={idx} value={idx} disabled={(() => {
                                                if (Array.isArray(v.product_stores) && v.product_stores.length > 0) {
                                                    return v.product_stores.reduce((sum, ps) => sum + (ps.quantity || 0), 0) === 0;
                                                }
                                                return v.quantity === 0;
                                            })()}>
                                                {v.size} / {v.color} ({(() => {
                                                    if (Array.isArray(v.product_stores) && v.product_stores.length > 0) {
                                                        return v.product_stores.reduce((sum, ps) => sum + (ps.quantity || 0), 0);
                                                    }
                                                    return v.quantity;
                                                })()} шт.) — Магазин: {getStoreName(v) || 'не указан'}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </>
                        )}
                        {product.type === 'toy' && product.variants && product.variants.length > 0 && (
                            <>
                                <div className="size-table-container">
                                    <h4>Варианты:</h4>
                                    <Table striped bordered hover>
                                        <thead>
                                            <tr>
                                                <th>Название</th>
                                                <th>Магазин</th>
                                                <th>Количество</th>
                                                <th>Штрих‑код</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {product.variants.map((v, idx) => (
                                                <tr key={idx} style={{background: selectedVariantIdx === idx ? '#e6f7ff' : undefined}} onClick={() => { setSelectedVariantIdx(idx); }}>
                                                    <td>{v.name || '[нет названия]'}</td>
                                                    <td>{getStoreName(v)}</td>
                                                    <td>{v.quantity}</td>
                                                    <td>{v.barcode || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                <Form.Group className="mb-3">
                                    <Form.Label>Выберите вариант</Form.Label>
                                    <Form.Select
                                        value={selectedVariantIdx}
                                        onChange={e => setSelectedVariantIdx(Number(e.target.value))}
                                    >
                                        <option value={-1}>Выберите вариант</option>
                                        {product.variants.map((v, idx) => (
                                            <option key={idx} value={idx} disabled={(v.quantity || 0) === 0}>
                                                {`${v.name || '[нет названия]'} — ${v.quantity || 0} шт. — Магазин: ${getStoreName(v)}`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Количество</Form.Label>
                            <Form.Control
                                type="number"
                                min="1"
                                value={quantity}
                                onChange={(e) => setQuantity(parseInt(e.target.value))}
                            />
                        </Form.Group>
                        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                        <Button 
                            variant="primary"
                            size="lg"
                            className="add-to-basket-button"
                            onClick={handleAddToBasket}
                            disabled={isOutOfStock}
                        >
                            Добавить в корзину
                        </Button>
                        {isOutOfStock && (
                            <div style={{ color: 'red', marginTop: 8, fontWeight: 500 }}>
                                Товара нет в наличии
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Форма для отзыва теперь всегда отображается */}
            <Row className="mt-5">
                <Col md={8}>
                    <div className="rating-form">
                        <h3>Оставить отзыв</h3>
                        {ratingError && <Alert variant="danger">{ratingError}</Alert>}
                        {ratingSuccess && <Alert variant="success">{ratingSuccess}</Alert>}
                        <Form onSubmit={handleRatingSubmit}>
                            <Form.Group className="mb-3">
                                <Form.Label>Ваша оценка</Form.Label>
                                <div className="rating-stars">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <FaStar
                                            key={star}
                                            size={30}
                                            color={star <= rating ? "#ffc107" : "#e4e5e9"}
                                            onClick={() => setRating(star)}
                                            style={{ cursor: 'pointer', marginRight: '5px' }}
                                        />
                                    ))}
                                </div>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Комментарий</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Расскажите о вашем опыте использования товара"
                                />
                            </Form.Group>
                            <Button variant="primary" type="submit">
                                Отправить отзыв
                            </Button>
                        </Form>
                    </div>
                </Col>
            </Row>

            {/* Отображение существующих отзывов */}
            {product.ratings && product.ratings.length > 0 && (
                <Row className="mt-5">
                    <Col md={12}>
                        <h3>Отзывы покупателей</h3>
                        <div className="ratings-list">
                            {product.ratings.map((rating, index) => (
                                <div key={index} className="rating-item mb-4 p-3 border rounded">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="rating-header">
                                            <div className="rating-stars mb-2">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <FaStar
                                                        key={star}
                                                        size={20}
                                                        color={star <= rating.score ? "#ffc107" : "#e4e5e9"}
                                                    />
                                                ))}
                                            </div>
                                            <div className="rating-user">
                                                <strong>{rating.userName}</strong>
                                                <small className="text-muted ms-2">
                                                    {new Date(rating.createdAt).toLocaleDateString()}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                    {rating.comment && (
                                        <div className="rating-comment mt-2">
                                            {rating.comment}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Col>
                </Row>
            )}

            {/* Отображение среднего рейтинга */}
            {product.averageRating > 0 && (
                <Row className="mt-3">
                    <Col md={12}>
                        <div className="average-rating">
                            <h4>Средняя оценка: {product.averageRating.toFixed(1)}</h4>
                            <div className="rating-stars">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <FaStar
                                        key={star}
                                        size={24}
                                        color={star <= Math.round(product.averageRating) ? "#ffc107" : "#e4e5e9"}
                                    />
                                ))}
                            </div>
                            <small className="text-muted">
                                На основе {product.ratings?.length || 0} отзывов
                            </small>
                        </div>
                    </Col>
                </Row>
            )}
        </Container>
    );
});

export default ProductPage;